import Loading from '@/components/Loading';
import { Dropdown, DropdownMenuDivider, DropdownMenuItem } from '@/components/UI/Dropdown';
import Link from '@/components/UI/Link';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { errorToast, successToast } from '@/helpers';
import { _isNil } from '@/littledash';
import type { DataTableApiId, DataTableTemplateInfoV1, DataTableType, DataTableV1 } from '@/model/DataTable.model';
import type { Study, StudyApiId } from '@/model/Study.model';
import { RiMoreFill } from 'react-icons/ri';
import { useNavigate, useParams } from 'react-router-dom';
import * as Auth from '@/support/auth';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { RouteService } from '@/support/RouteService';
import { useModalAction } from '@/utils/modal';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook.ts';
import ActionList, { type ActionProps } from '@/components/UI/SelectDropDown/Menus/ActionList.tsx';
import SelectDropDown from '@/components/UI/SelectDropDown';
import { useMemo } from 'react';

const displayTypes: Array<DataTableType> = ['custom', 'numeric', 'time'];

export const DataTablesList = () => {
  const { openModal } = useModalAction();
  const { id: studyId } = useParams();

  const { response: studyResponse, loading: studyLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.show.p',
    path: { id: studyId },
    query: { include: 'users' },
    options: { onError: { toast: true, slug: 'study-load' } },
  });

  const { response: templatesResponse, loading: templateLoading } = useApiHook({
    endpoint: 'GET /api/v1/datatables/templates',
    invokeOnInit: true,
    query: { perPage: 1, page: 1 },
    options: { onError: { throw: false, capture: true, toast: false } },
  });

  const study = (studyResponse?.body as { data: Study })?.data;

  const templatesExist = (templatesResponse?.body as { data: DataTableTemplateInfoV1[] })?.data.length > 0;

  const isStudyWriteUser = Auth.isWriteUserForStudy(study as Study);

  const columns: Array<TanStackColumnDef<DataTableV1>> = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name',
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: DataTableV1 } }) =>
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          original && (
            <Link
              to={
                RouteService.web({
                  route: 'studies.dataTables.show',
                  path: { id: studyId, dataTableApiId: original.id },
                }).route
              }
              className="link blue"
            >
              {original.name}
            </Link>
          ),
      },
      {
        id: 'created_at',
        header: 'Created on',
        accessorKey: 'created_at',
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: DataTableV1 } }) =>
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          original ? <DateTimeRenderer value={original.created_at} /> : null,
      },
      {
        id: 'type',
        header: 'Type',
        accessorKey: 'type',
        isVisible: true,
        enableSorting: false,
      },
      {
        id: 'animal_count',
        header: 'Animals',
        accessorFn: (row: DataTableV1) => (row as any).animal_count,
        isVisible: true,
        enableSorting: false,
      },
      {
        id: 'actions',
        isVisible: isStudyWriteUser,
        size: 35,
        enableSorting: false,
        cell: ({ table, row: { original } }: { table?: any; row: { original: DataTableV1 } }) =>
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          original ? (
            <Dropdown
              renderMenu={() => (
                <>
                  <DropdownMenuItem
                    onClick={() =>
                      handleDataTableEdit(
                        original.id as DataTableApiId,
                        table?.options?.meta?.apiTable?.fetchTableData as () => Promise<void>
                      )
                    }
                  >
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuDivider />
                  <DropdownMenuItem
                    onClick={() =>
                      handleDataTableDelete(
                        original.id as DataTableApiId,
                        table?.options?.meta?.apiTable?.fetchTableData as () => Promise<void>
                      )
                    }
                  >
                    <span className="red">Delete</span>
                  </DropdownMenuItem>
                </>
              )}
            >
              <RiMoreFill size={22} />
            </Dropdown>
          ) : null,
      },
    ],
    [isStudyWriteUser, study, studyId]
  );

  const { invoke: invokeDeleteDataTable } = useApiHook({
    endpoint: 'DELETE /api/v1/studies/{studyApiId}/datatables/{dataTableApiId}',
    path: { studyApiId: study?.api_id as StudyApiId, dataTableApiId: 'dtl_empty' },
    invokeOnInit: false,
  });
  const navigate = useNavigate();
  const navigateToCreateDataTable = () => {
    navigate(
      RouteService.web({
        route: 'studies.dataTables.create',
        path: { id: studyId },
      }).route
    );
  };
  const navigateToCreateDataTableFromTemplate = () => {
    navigate(
      RouteService.web({
        route: 'studies.templates.dataTables.create',
        path: { id: studyId },
      }).route
    );
  };
  const handleDataTableEdit = (dataTableId: DataTableApiId, fetchTableData: () => Promise<void>): void => {
    openModal('EDIT_DATATABLE', {
      studyId: study?.api_id ?? '',
      dataTableId,
      fetchTableData,
      mode: 'datatable',
    });
  };
  const handleDataTableDelete = (dataTableId: DataTableApiId, fetchTableData: () => Promise<void>): void => {
    openModal('CONFIRM_DELETE_DATATABLE', {
      onClick: async () => {
        try {
          await invokeDeleteDataTable({
            path: { studyApiId: study?.api_id as StudyApiId, dataTableApiId: dataTableId },
          });
          await fetchTableData();
          successToast('Deleted datatable');
        } catch (err) {
          const ivError = new InVivoError('Could not delete data table', {
            slug: 'delete-data-table',
            cause: err,
          });
          ExceptionHandler.captureException(ivError);
          errorToast('Could not delete data table');
        }
      },
    });
  };

  if (studyLoading || templateLoading || _isNil(study)) {
    return (
      <div style={{ height: 600 }}>
        <Loading txt="Loading data tables" />
      </div>
    );
  }

  const actions: ActionProps[] = [
    {
      name: 'Add new',
      action: () => navigateToCreateDataTable(),
    },
    {
      name: 'Create using template',
      action: () => navigateToCreateDataTableFromTemplate(),
      disabled: !templatesExist,
      tooltip: 'No available templates exist on this tenant',
    },
  ];

  return (
    <div className="h-100">
      <div className="">
        <APITable
          apiInfo={{
            type: 'internalApi',
            route: RouteService.api({
              endpoint: 'GET /api/v1/studies/{studyApiId}/datatables',
              path: { studyApiId: study?.api_id },
              query: { types: displayTypes },
            }).url,
          }}
          columns={columns}
          defaultSortBy={{ id: 'created_at', desc: false }}
          searchPlaceholderText="Search"
          reduxTableName={'dataTables'}
          testIdPrefix={'dataTables'}
          AsideComponent={() =>
            isStudyWriteUser ? (
              <SelectDropDown
                title="Create new Data table"
                alignMenu="right"
                className="pr4"
                testId="create-new-datatable-select-dropdown"
              >
                <ActionList actions={actions} />
              </SelectDropDown>
            ) : null
          }
        />
      </div>
    </div>
  );
};
